import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type RefObject,
} from 'react';

import { Button, focusRing, Icon, MonoLabel } from '@/design-system';
import type {
    PublicDocument,
    PublicFullReport,
    PublicLanguage,
    PublicReport,
} from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatDate } from '../format';
import { jumpTo } from '../jump';
import { appendixKeys, chapterVisible, type IndexedEntry } from '../reportIndex';
import type { FullReportState } from '../useFullReportState';
import type { PublicDocumentState } from '../usePublicReport';
import { chapterAnchor } from '../useReportView';
import { DocumentLink, DocumentUnavailable } from './DocumentLink';
import { ReportChapter } from './ReportChapter';
import { ReportToolbar } from './ReportToolbar';

export interface FullReportProps {
    report: PublicReport;
    fullReport: PublicFullReport;
    /** Filter, dichte hoofdstukken en een sprong die nog moet gebeuren; leeft boven de tabbladen. */
    state: FullReportState;
    language: PublicLanguage;
    copy: PublicCopy;
    /** `1` op de publieke pagina; `2` in de kantoorpreview. */
    headingLevel: 1 | 2;
    /** Zet de plek in de adresbalk, zonder nieuwe stap; `null` is bovenaan. */
    onTargetChange: (key: string | null) => void;
    /** Het hoofdstuk dat nu gelezen wordt; `null` op het voorblad. */
    onActiveChange: (key: string | null) => void;
    onBackToOverview: () => void;
    /** Naar de regel van een NHG-code in de bijlage, vanaf deze bevinding. */
    onNhgJump: (code: string, from: IndexedEntry | null) => void;
    brandStyle: CSSProperties;
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    documentState?: PublicDocumentState;
    /** Blijft de balk onder de tabbladen staan tijdens het lezen? Zie {@link ReportTabsProps.pinned}. */
    pinned?: boolean;
    onLinkExpired?: () => void;
}

/**
 * Het tabblad Rapport: het volledige geschreven rapport, met dezelfde inhoud als
 * de PDF, als rustig document om op het scherm te lezen.
 *
 * - Bovenaan een smalle balk die blijft staan: Terug naar overzicht, en het
 *   hoofdstuk dat nu gelezen wordt. De weg door het rapport is het overzicht.
 * - Een filterbalk (Alles, Gebreken, Aandachtspunten, Met kosten) met aantallen;
 *   een filter laat de koppen staan, zodat te zien blijft waar iets zit.
 * - Elk hoofdstuk staat open, met in de kop het oordeel en het aantal
 *   bevindingen; de koper kan er een dichtklappen. Een link naar een hoofdstuk
 *   of kop (`#rapport-6-2`) springt daarheen en licht hem even op.
 * - De NHG-code onder een bevinding opent het tabblad NHG bij zijn regel.
 */
export function FullReport({
    report,
    fullReport,
    state,
    language,
    copy,
    headingLevel,
    onTargetChange,
    onActiveChange,
    onBackToOverview,
    onNhgJump,
    brandStyle,
    pinned = true,
    onOpenDocument,
    documentState,
    onLinkExpired,
}: FullReportProps) {
    const { index, filter, target, revealed } = state;
    const chapters = fullReport.chapters.filter(
        (item) => item.kind !== 'nhg' && chapterVisible(item, index, filter),
    );
    const topRef = useRef<HTMLElement | null>(null);
    const titleRef = useRef<HTMLHeadingElement | null>(null);
    const active = useActiveChapter(chapters.map((item) => item.key));
    const level = headingLevel === 1 ? 2 : 3;
    /* Waar de bijlage begint; met een filter staat er geen enkel bijlagehoofdstuk, en dan ook geen kop. */
    const appendix = useMemo(() => appendixKeys(fullReport), [fullReport]);
    const appendixFrom = chapters.findIndex((item) => appendix.has(item.key));

    useEffect(() => {
        onActiveChange(active);
    }, [active, onActiveChange]);

    /* Het vasthouden en oplichten van een doel; een nieuwe sprong of weggaan stopt het. */
    const release = useRef<() => void>(() => undefined);

    useEffect(() => {
        const current = release;

        return () => current.current();
    }, []);

    /* Een sprong gebeurt zodra het hoofdstuk open en in beeld is. */
    useEffect(() => {
        if (target === null) {
            return;
        }

        release.current();
        release.current = jumpTo(target.anchor, { heading: target.heading, hold: true });
        revealed();
    }, [target, revealed]);

    /* Naar het begin van de pagina, met de tabbladen weer in beeld. */
    const toTop = useCallback(() => {
        release.current();
        (topRef.current?.closest('article') ?? topRef.current)?.scrollIntoView?.({
            block: 'start',
        });
        titleRef.current?.focus({ preventScroll: true });
        onTargetChange(null);
    }, [onTargetChange]);

    const current = chapters.find((item) => item.key === active) ?? null;

    return (
        <div>
            {/* Onder de tabbladen blijft deze balk staan: terug naar het overzicht, en waar de lezer is. */}
            <div
                className={cn(
                    'flex min-h-[45px] items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--surface-card)] px-[calc(var(--pr-pad-x)_-_8px)]',
                    pinned && 'sticky top-[49px] z-10',
                )}
            >
                <button
                    type="button"
                    onClick={onBackToOverview}
                    className={cn(
                        'inline-flex min-h-[44px] shrink-0 cursor-pointer items-center gap-[6px] rounded-[7px] border-0 bg-transparent px-2',
                        'font-[family-name:var(--font-body)] text-[13px] leading-none font-medium text-[var(--accent-dark)] transition-colors duration-100 hover:text-[var(--accent)]',
                        focusRing,
                        'focus-visible:-outline-offset-2',
                    )}
                >
                    <Icon name="back" size="sm" />
                    {copy.backToOverview}
                </button>

                {current ? (
                    <>
                        <span
                            aria-hidden="true"
                            className="h-4 w-px shrink-0 bg-[var(--border-default)]"
                        />
                        <span
                            aria-hidden="true"
                            className="min-w-0 flex-1 truncate px-1 text-[13px] leading-[1.3] text-[var(--text-secondary)]"
                        >
                            {current.number ? (
                                <span className="mr-[6px] font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-muted-mono-safe)]">
                                    {current.number}
                                </span>
                            ) : null}
                            {current.title}
                        </span>
                    </>
                ) : null}
            </div>

            <ReportCover
                fullReport={fullReport}
                documents={report.documents}
                language={language}
                copy={copy}
                headingLevel={headingLevel}
                topRef={topRef}
                titleRef={titleRef}
                {...(onOpenDocument ? { onOpenDocument } : {})}
                {...(documentState ? { documentState } : {})}
            />

            <div className="px-[var(--pr-pad-x)] pt-6 pb-8">
                <ReportToolbar
                    filter={filter}
                    onFilter={state.setFilter}
                    counts={index.counts}
                    total={index.findingCount}
                    showCosts={report.costs.visible}
                    allOpen={state.allOpen}
                    onAllOpen={state.setAllOpen}
                    copy={copy}
                />

                {chapters.length === 0 ? (
                    <p className="m-0 border-t border-[var(--border-subtle)] py-5 text-[15px] leading-[1.6] text-[var(--text-tertiary-safe)] italic">
                        {copy.filterEmpty}
                    </p>
                ) : null}

                {chapters.map((item, position) => (
                    <Fragment key={item.key}>
                        {position === appendixFrom ? (
                            <AppendixLead copy={copy} level={level} />
                        ) : null}

                        <ReportChapter
                            chapter={item}
                            costs={report.costs}
                            level={level}
                            language={language}
                            copy={copy}
                            brandStyle={brandStyle}
                            open={state.isOpen(item.key)}
                            onToggle={() => state.toggle(item.key)}
                            index={index}
                            filter={filter}
                            onNhgJump={onNhgJump}
                            {...(onLinkExpired ? { onLinkExpired } : {})}
                        />
                    </Fragment>
                ))}

                <div className="mt-4 border-t border-[var(--border-subtle)] pt-4">
                    <Button variant="outline" size="md" onClick={toTop}>
                        {copy.backToTop}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/**
 * De scheiding tussen het rapport over deze woning en de bijlage erna.
 *
 * Alles wat de koper zoekt - bevindingen, gebreken, foto's, adviezen - staat
 * erboven; de algemene toelichting en de voorwaarden eronder. Dezelfde bijlage
 * als in de PDF, met dezelfde titel.
 */
function AppendixLead({ copy, level }: { copy: PublicCopy; level: 2 | 3 }) {
    const Heading = level === 2 ? 'h2' : 'h3';

    return (
        <div className="mt-8 border-t-2 border-[var(--border-default)] pt-6">
            <Heading className="m-0 font-[family-name:var(--font-display)] text-[19px] leading-[1.25] font-semibold tracking-[-0.015em] text-[var(--text-primary)]">
                {copy.appendixTitle}
            </Heading>
            <p className="mt-[6px] mb-2 text-[14px] leading-[1.55] text-[var(--text-tertiary-safe)]">
                {copy.appendixNote}
            </p>
        </div>
    );
}

/**
 * Het voorblad, zoals in de PDF: soort keuring, adres, datum, inspecteur,
 * dossiernummer en versie, met de PDF ernaast.
 */
function ReportCover({
    fullReport,
    documents,
    language,
    copy,
    headingLevel,
    topRef,
    titleRef,
    onOpenDocument,
    documentState,
}: {
    fullReport: PublicFullReport;
    documents: PublicDocument[];
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 1 | 2;
    topRef: RefObject<HTMLElement | null>;
    titleRef: RefObject<HTMLHeadingElement | null>;
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    documentState?: PublicDocumentState;
    /** Blijft de balk onder de tabbladen staan tijdens het lezen? Zie {@link ReportTabsProps.pinned}. */
    pinned?: boolean;
}) {
    const { cover } = fullReport;
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const pdf = documents[0] ?? null;

    const facts: Array<[string, string | null]> = [
        [copy.coverInspectedAt, formatDate(cover.inspectedAt, language)],
        [copy.coverInspector, cover.inspector],
        [copy.coverOrderNumber, cover.orderNumber],
        [
            copy.coverReport,
            copy.coverVersion(cover.version, formatDate(cover.generatedAt, language)),
        ],
    ];

    return (
        <header
            ref={topRef}
            className="border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] pt-7 pb-6"
        >
            <MonoLabel size={11} tone="muted" as="div" className="mb-3">
                {[cover.kind, cover.norm].filter(Boolean).join(' · ')}
            </MonoLabel>

            <Heading
                ref={titleRef}
                tabIndex={-1}
                className="m-0 font-[family-name:var(--font-display)] text-[clamp(24px,4.4cqi,30px)] leading-[1.1] font-semibold tracking-[-0.025em] text-pretty text-[var(--text-primary)] outline-none"
            >
                {cover.street}
                <br />
                {cover.place}
            </Heading>

            <dl className="m-0 mt-4 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-5 gap-y-[6px]">
                {facts.map(([label, value]) =>
                    value ? (
                        <Fragment key={label}>
                            <dt className="text-[13px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                                {label}
                            </dt>
                            <dd className="m-0 text-[13px] leading-[1.45] text-[var(--text-primary)]">
                                {value}
                            </dd>
                        </Fragment>
                    ) : null,
                )}
            </dl>

            {pdf ? (
                <DocumentLink
                    document={pdf}
                    onOpenDocument={onOpenDocument}
                    busy={documentState?.busy === pdf.key}
                    variant="outline"
                    size="md"
                    copy={copy}
                    className="mt-5"
                />
            ) : null}

            {pdf && documentState?.failed === pdf.key ? <DocumentUnavailable copy={copy} /> : null}
        </header>
    );
}

/**
 * Welk hoofdstuk nu gelezen wordt: het eerste waarvan een deel in de strook
 * onder de vaste balken staat. Op het voorblad nog geen; zonder
 * IntersectionObserver (oude browser, tests) ook niet.
 */
function useActiveChapter(keys: string[]): string | null {
    const signature = JSON.stringify(keys);
    const [active, setActive] = useState<string | null>(null);

    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') {
            return;
        }

        const ordered = JSON.parse(signature) as string[];
        const visible = new Set<string>();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        visible.add(entry.target.id);
                    } else {
                        visible.delete(entry.target.id);
                    }
                }

                setActive(ordered.find((key) => visible.has(chapterAnchor(key))) ?? null);
            },
            { rootMargin: '-15% 0px -65% 0px' },
        );

        for (const key of ordered) {
            const section = document.getElementById(chapterAnchor(key));

            if (section) {
                observer.observe(section);
            }
        }

        return () => observer.disconnect();
    }, [signature]);

    return active;
}
